import type { FilingGroupResult, FilingTargetResultStatus } from "../api/types";

const STATUS_STYLES: Record<FilingTargetResultStatus, string> = {
  filed: "bg-green-100 text-green-800",
  partial: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-800",
};

interface FilingProgressProps {
  result: FilingGroupResult;
  projectLabels: Map<number, string>;
}

export function FilingProgress({ result, projectLabels }: FilingProgressProps) {
  const filedCount = result.targets.filter((t) => t.status === "filed").length;
  const total = result.targets.length;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium text-slate-700">
        {filedCount} of {total} project{total === 1 ? "" : "s"} filed
        successfully
      </p>
      <ul className="flex flex-col gap-2">
        {result.targets.map((target) => (
          <li
            key={target.project_id}
            className="rounded-md border border-slate-200 p-2"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-slate-900">
                {projectLabels.get(target.project_id) ??
                  `Project #${target.project_id}`}
              </span>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[target.status]}`}
              >
                {target.status}
              </span>
            </div>
            {target.duplicate && (
              <p className="mt-1 text-xs text-amber-700">
                Filed anyway despite an existing duplicate.
              </p>
            )}
            <p className="mt-1 text-xs text-slate-500">{target.dest_path}</p>
            <details className="mt-1">
              <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700">
                {target.files.length} file
                {target.files.length === 1 ? "" : "s"}
              </summary>
              <ul className="mt-1 flex flex-col gap-1 pl-3">
                {target.files.map((file) => (
                  <li
                    key={`${target.project_id}-${file.kind}-${file.filename}`}
                    className="text-xs"
                  >
                    <span
                      className={
                        file.status === "failed"
                          ? "text-red-700"
                          : "text-slate-600"
                      }
                    >
                      {file.filename}
                    </span>
                    {file.error && (
                      <span className="ml-1 text-red-600">— {file.error}</span>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          </li>
        ))}
      </ul>
    </div>
  );
}
